import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import FoodShell from "../../layouts/FoodShell";
import { appModules } from "../../components/global";
import { GET, POST, DELETE, Select, alert, loading, DateInput } from "../../components/CustomComponent";
import { mealSlots } from "./mockData";
import { Panel, Section, KpiStrip, MacroBar, NutritionLine, Chip, Skeleton, todayIso, shiftIso, thaiDateOf, currentMealKey } from "./FoodUi";

const modalAnimation = !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
const mealOptions = mealSlots.map((slot) => ({ value: slot.key, label: slot.label }));
const dayTones = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MACROS = [
  ["kcal", "Calories", "fa-solid fa-fire", "kcal", "orange"],
  ["protein", "Protein", "fa-solid fa-drumstick-bite", "g", "red"],
  ["carbs", "Carbs", "fa-solid fa-bowl-rice", "g", "gold"],
  ["fat", "Fat", "fa-solid fa-seedling", "g", "green"],
];

// บันทึกอาหารที่กิน: GET v1/food/diary?date= คำขอเดียวได้มื้อของวัน + สรุป + 7 วัน + insight
// มื้อส่วนใหญ่ถูกบันทึกอัตโนมัติจากปุ่ม "กินอันนี้" หน้านี้มีไว้ดูและเพิ่มเองเป็นทางเลือก
export default function FoodDiary() {
  const { title, description } = appModules["food_diary"];
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [foods, setFoods] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  // foodUids: เลือกได้หลายเมนูต่อมื้อ บันทึกทีเดียวด้วย POST เดียว (food_uids)
  const [form, setForm] = useState({ meal: "dinner", foodUids: [], search: "", note: "" });
  const [customBusy, setCustomBusy] = useState(false);

  const load = useCallback(async (day) => {
    const result = await GET("v1/food/diary", { date: day });
    if (result?.status === true) setData(result.data);
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const openAdd = async (meal) => {
    setForm({ meal: meal || (date === todayIso() ? currentMealKey() : "dinner"), foodUids: [], search: "", note: "" });
    setShowAdd(true);
    // โหลดฐานข้อมูลเมนูครั้งเดียวต่อหน้า (backend cache ไว้แล้ว) แล้วค้นหาในเครื่อง
    if (!foods) {
      const result = await GET("v1/food/foods");
      if (result?.status === true) setFoods(result.data.items);
    }
  };

  const toggleFood = (uid) => setForm((current) => ({ ...current, foodUids: current.foodUids.includes(uid) ? current.foodUids.filter((item) => item !== uid) : [...current.foodUids, uid] }));

  const saveEntry = async () => {
    if (!form.foodUids.length) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาเลือกเมนูที่กินอย่างน้อย 1 อย่าง");
      return;
    }
    loading(true);
    const result = await POST("v1/food/meal-logs", { food_uids: form.foodUids, meal: form.meal, eaten_on: date, source: "manual", note: form.note });
    loading(false);
    if (result?.status === true) {
      setShowAdd(false);
      load(date);
    }
  };

  // พิมพ์ชื่อเมนูที่ไม่มีในรายการ: backend หาเมนูชื่อเดียวกัน หรือให้ AI ประเมินสารอาหารแล้วเพิ่มเข้าฐานให้เอง
  const saveCustom = async () => {
    const name = form.search.trim();
    if (!name) return;
    setCustomBusy(true);
    const result = await POST("v1/food/meal-logs/custom", { name, meal: form.meal, eaten_on: date, note: form.note });
    setCustomBusy(false);
    if (result?.status === true) {
      setShowAdd(false);
      setFoods(null);
      const food = result.data.food;
      await alert("", "success", result.data.created ? "AI เพิ่มเมนูใหม่ให้แล้ว" : "บันทึกแล้ว", `${food.emoji || ""} ${food.name} · ${food.kcal} kcal · โปรตีน ${food.protein} g · ประมาณ ฿${food.price}${result.data.created ? "\n(ค่าประมาณจาก AI แก้ไขได้ที่หน้าฐานข้อมูลเมนู)" : ""}`);
      load(date);
    }
  };

  const removeEntry = async (entry) => {
    if (!(await alert("", "warning", "ลบรายการ", `ลบ "${entry.food.name}" ออกจากบันทึกของวันนี้ ?`, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const result = await DELETE(`v1/food/meal-logs/${entry.uid}`);
    loading(false);
    if (result?.status === true) load(date);
  };

  const keyword = form.search.trim().toLowerCase();
  const searchResults = (foods || []).filter((item) => item.is_active && (!keyword || item.name.toLowerCase().includes(keyword) || item.tags.some((tag) => tag.toLowerCase().includes(keyword))));
  const selectedFoods = form.foodUids.map((uid) => (foods || []).find((item) => item.uid === uid)).filter(Boolean);
  const selectedSum = (key) => selectedFoods.reduce((total, item) => total + (item[key] || 0), 0);
  const logs = data?.logs || [];
  const totals = data?.totals || { kcal: 0, protein: 0, carbs: 0, fat: 0, price: 0, count: 0 };
  const isToday = date === todayIso();

  return (
    <FoodShell title={title} description={description}>
      <div className="row g-3">
        <div className="col-xl-8">
          <Panel
            eyebrow="Food diary"
            icon="fa-solid fa-book-open"
            title={isToday ? "มื้ออาหารวันนี้" : "มื้ออาหาร"}
            desc={thaiDateOf(date)}
            actions={
              <>
                <button type="button" className="stg-icon-btn" title="วันก่อนหน้า" onClick={() => setDate(shiftIso(date, -1))}><i className="fa-solid fa-chevron-left" /></button>
                <DateInput className="form-control" style={{ width: 160 }} value={date} max={todayIso()} onChange={(event) => event.target.value && setDate(event.target.value)} />
                <button type="button" className="stg-icon-btn" title="วันถัดไป" disabled={isToday} onClick={() => setDate(shiftIso(date, 1))}><i className="fa-solid fa-chevron-right" /></button>
                <button type="button" className="btn stg-btn stg-btn--primary" onClick={() => openAdd()}><i className="fa-solid fa-plus" />บันทึกมื้อที่กิน</button>
              </>
            }
          >
            <KpiStrip
              className="mb-3"
              items={[
                ["orange", "fa-solid fa-fire", totals.kcal, "kcal", `เป้าหมาย ${(data?.targets?.kcal || 0).toLocaleString()} kcal`],
                ["red", "fa-solid fa-drumstick-bite", `${totals.protein} g`, "โปรตีน", `เป้าหมาย ${data?.targets?.protein || 0} g`],
                ["gold", "fa-solid fa-coins", `฿${totals.price}`, "ใช้จ่าย"],
                ["green", "fa-solid fa-list-check", totals.count, "รายการ"],
              ]}
            />

            {!data ? <Skeleton lines={6} /> : (
              <div className="food-timeline">
                {mealSlots.map((slot) => {
                  const items = logs.filter((item) => item.meal === slot.key);
                  const sum = (key) => items.reduce((total, item) => total + item.food[key], 0);
                  return (
                    <div key={slot.key} className={`food-slot ${items.length ? "" : "is-empty"}`}>
                      <div className="food-slot-head">
                        <span className="food-slot-icon"><i className={slot.icon} /></span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="food-slot-title">{slot.label} <small>· ปกติ {slot.time}</small></div>
                          {items.length ? <div className="food-slot-sub">{sum("kcal")} kcal · โปรตีน {sum("protein")} g · ฿{sum("price")}</div> : <div className="food-slot-sub">ยังไม่ได้บันทึก</div>}
                        </div>
                        <button type="button" className="stg-icon-btn" title={`เพิ่ม${slot.label}`} onClick={() => openAdd(slot.key)}><i className="fa-solid fa-plus" /></button>
                      </div>
                      {items.map((entry) => (
                        <div key={entry.uid} className="food-entry">
                          <span className="food-entry-emoji">{entry.food.emoji}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="food-entry-name">
                              {entry.food.name} {entry.eaten_time ? <span className="food-entry-time">{entry.eaten_time}</span> : null}
                              {entry.source !== "manual" ? <Chip tone="orange" icon="fa-solid fa-robot" className="ms-2">AI แนะนำ</Chip> : null}
                            </div>
                            <NutritionLine food={entry.food} />
                            {entry.note ? <div className="food-entry-note"><i className="fa-regular fa-note-sticky" />{entry.note}</div> : null}
                          </div>
                          <button type="button" className="stg-icon-btn stg-icon-btn--danger" title="ลบ" onClick={() => removeEntry(entry)}><i className="fa-solid fa-trash" /></button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>

        <div className="col-xl-4">
          <Section icon="fa-solid fa-chart-simple" title="Daily Nutrition" desc="เทียบกับเป้าหมายจากโปรไฟล์" className="mb-3">
            <div className="d-grid gap-2">
              {MACROS.map(([key, label, icon, unit, tone]) => (
                <MacroBar key={key} label={label} icon={icon} unit={unit} tone={tone} used={totals[key] || 0} target={data?.targets?.[key] || 1} />
              ))}
            </div>
            {data?.tip ? <p className="food-tip mt-3"><i className="fa-solid fa-robot" />{data.tip}</p> : null}
          </Section>

          <Section icon="fa-solid fa-calendar-week" title="7 วันล่าสุด" desc="มื้อหลักที่ใหญ่ที่สุดของแต่ละวัน" className="mb-3">
            {data ? (
              <div className="food-week">
                {data.week.map((day) => {
                  const [year, month, dayNo] = day.date.split("-").map(Number);
                  const weekday = new Date(year, month - 1, dayNo).getDay();
                  return (
                    <div key={day.date} className="food-week-row" role="button" onClick={() => setDate(day.date)} style={{ cursor: "pointer" }}>
                      <span className={`food-day food-day--${dayTones[weekday]}`}>{thaiDateOf(day.date, "weekday")}</span>
                      <span className="food-week-food">{day.main ? `${day.main.food.emoji || ""} ${day.main.food.name}` : <span className="text-secondary">ไม่มีบันทึก</span>}</span>
                      {day.count ? <Chip tone="muted">{day.kcal} kcal</Chip> : null}
                    </div>
                  );
                })}
              </div>
            ) : <Skeleton />}
          </Section>

          <Section icon="fa-solid fa-brain" title="AI เรียนรู้ว่า" desc="รูปแบบการกินที่ระบบสังเกตได้ คำแนะนำจะฉลาดขึ้นเรื่อย ๆ">
            {data?.insights?.length ? (
              <div className="d-grid gap-2">
                {data.insights.map((item) => (
                  <div key={item.text} className="food-insight">
                    <span className="food-insight-icon"><i className={item.icon} /></span>
                    <div><strong>{item.text}</strong><small>{item.detail}</small></div>
                  </div>
                ))}
              </div>
            ) : <p className="food-tip"><i className="fa-solid fa-seedling" />ยังไม่มีข้อมูลพอ บันทึกมื้อสัก 2-3 วันระบบจะเริ่มเห็นรูปแบบ</p>}
          </Section>
        </div>
      </div>

      <Modal animation={modalAnimation} show={showAdd} onHide={() => setShowAdd(false)} centered size="lg" dialogClassName="stg-modal food-modal modal-dialog-scrollable">
        <Modal.Header closeButton>
          <div>
            <p className="stg-eyebrow">Food diary · {thaiDateOf(date, "short")}</p>
            <Modal.Title>บันทึกมื้อที่กิน</Modal.Title>
            <p className="stg-modal-sub">เลือกได้หลายเมนูต่อมื้อ ระบบจะคำนวณสารอาหารและค่าใช้จ่ายให้อัตโนมัติ</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="stg-label">มื้อ</label>
              <Select className="form-control" value={form.meal} options={mealOptions} onChange={(value) => setForm({ ...form, meal: value })} />
            </div>
            <div className="col-md-8">
              <label className="stg-label">ค้นหาเมนู</label>
              <div className="stg-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input type="text" className="form-control" value={form.search} onChange={(event) => setForm({ ...form, search: event.target.value })} placeholder="พิมพ์ชื่อเมนูหรือแท็ก เช่น ไก่, คลีน..." />
              </div>
            </div>
            {selectedFoods.length ? (
              <div className="col-12">
                <div className="food-picked">
                  <div className="food-picked-head">
                    <span><i className="fa-solid fa-utensils" />เลือกแล้ว <strong>{selectedFoods.length}</strong> เมนู · {selectedSum("kcal").toLocaleString()} kcal · โปรตีน {selectedSum("protein")} g · ฿{selectedSum("price").toLocaleString()}</span>
                    <button type="button" className="food-picked-clear" onClick={() => setForm({ ...form, foodUids: [] })}>ล้าง</button>
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {selectedFoods.map((item) => (
                      <Chip key={item.uid} tone="orange" icon="fa-solid fa-xmark" onClick={() => toggleFood(item.uid)}>{item.emoji} {item.name}</Chip>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="col-12">
              <div className="food-picker">
                {!foods ? <Skeleton lines={4} /> : searchResults.length ? searchResults.map((item) => {
                  const picked = form.foodUids.includes(item.uid);
                  return (
                    <button key={item.uid} type="button" className={`food-picker-item ${picked ? "is-active" : ""}`} onClick={() => toggleFood(item.uid)}>
                      <span className="food-card-emoji">{item.emoji}</span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span className="food-card-name d-block">{item.name}</span>
                        <NutritionLine food={item} />
                      </span>
                      <i className={`food-picker-check ${picked ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}`} />
                    </button>
                  );
                }) : null}
                {foods && keyword ? (
                  <button type="button" className="food-picker-item food-picker-item--custom" onClick={saveCustom} disabled={customBusy}>
                    <span className="food-card-emoji"><i className={customBusy ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-wand-magic-sparkles"} /></span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span className="food-card-name d-block">{searchResults.length ? "ไม่ใช่เมนูพวกนี้? " : ""}บันทึก "{form.search.trim()}" เป็นเมนูใหม่</span>
                      <span className="stg-cell-sub">AI จะประเมิน kcal / โปรตีน / ราคา ให้ และเพิ่มเข้าฐานข้อมูลเมนูอัตโนมัติ</span>
                    </span>
                  </button>
                ) : null}
                {foods && !searchResults.length && !keyword ? <div className="stg-empty"><i className="fa-regular fa-folder-open" />ยังไม่มีเมนู</div> : null}
              </div>
            </div>
            <div className="col-12">
              <label className="stg-label">บันทึกเพิ่มเติม</label>
              <input type="text" className="form-control" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="เช่น ร้านข้างออฟฟิศ, ไม่ใส่น้ำตาล" />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setShowAdd(false)}>ยกเลิก</button>
          <button type="button" className="btn stg-btn stg-btn--primary" onClick={saveEntry}><i className="fa-solid fa-floppy-disk" />บันทึก{form.foodUids.length ? ` ${form.foodUids.length} เมนู` : ""}</button>
        </Modal.Footer>
      </Modal>
    </FoodShell>
  );
}
